/**
 * The kiosk's data layer. It COMPOSES existing household-wide, non-money endpoints — never
 * money, never an adults-only route (a kiosk on the kitchen counter is visible to everyone).
 * Everything goes through `api` (src/lib/api.ts); no raw fetch. Each read is independent so
 * one failure only darkens its own tile.
 */
import { api } from '../../lib/api';
import type {
  CalendarEvent,
  MealPlanEntry,
  Message,
  SpecialDate,
  Weather,
} from '../../shared/types';
import { weekStart } from '../../lib/api';

export const getWeather = () => api.get<Weather>('/weather');

/** Today's dinner from the week's plan. */
export async function getTonightDinner(now = new Date()): Promise<MealPlanEntry | null> {
  const monday = weekStart(now);
  const plan = await api.get<MealPlanEntry[]>(
    `/meals/plan?week_start=${encodeURIComponent(monday)}`
  );
  const today = localIso(now);
  return plan.find((e) => e.plan_date === today && e.slot === 'dinner') ?? null;
}

/** The next thing on the calendar, from today through the next two weeks. The endpoint
 *  takes inclusive YYYY-MM-DD dates; the time-of-day "hasn't ended yet" cut happens here. */
export async function getNextEvent(now = new Date()): Promise<CalendarEvent | null> {
  const from = localIso(now);
  const to = localIso(new Date(now.getTime() + 14 * 86_400_000));
  const events = await api.get<CalendarEvent[]>(`/calendar?from=${from}&to=${to}`);
  return nextEvent(events, now);
}

/** The board's most recent note. */
export async function getLatestNote(): Promise<Message | null> {
  const notes = await api.get<Message[]>('/messages?limit=1');
  return notes[0] ?? null;
}

/** The nearest birthday/anniversary within the next week, if any. */
export async function getUpcomingDate(): Promise<SpecialDate | null> {
  const dates = await api.get<SpecialDate[]>('/birthdays');
  const soon = dates
    .filter((d) => d.days_away >= 0 && d.days_away <= 7)
    .sort((a, b) => a.days_away - b.days_away);
  return soon[0] ?? null;
}

// ---------------------------------------------------------------- helpers

function localIso(now: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
}

/**
 * The earliest event that hasn't finished. An all-day event counts while its day is today or
 * later; a timed event counts until it ends (or starts, if it has no end). Sorted by start.
 */
function nextEvent(events: CalendarEvent[], now: Date): CalendarEvent | null {
  const today = localIso(now);
  const live = events.filter((e) => {
    if (e.all_day) return e.starts_at.slice(0, 10) >= today;
    const end = e.ends_at ? new Date(e.ends_at) : new Date(e.starts_at);
    return end.getTime() >= now.getTime();
  });
  live.sort((a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());
  return live[0] ?? null;
}
